import { useEffect } from "react";

const findVideo = (container) => container?.querySelector("video") ?? null;

const POLL_MS = 300;
const MAX_ATTEMPTS = 20;

const useContinuousAutofocus = ({ containerRef, enabled = true }) => {
  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    let attempts = 0;
    let timeoutId = null;

    const applyFocus = async (track) => {
      const capabilities = track.getCapabilities?.();
      const modes = capabilities?.focusMode;
      if (!Array.isArray(modes) || !modes.includes("continuous")) return;

      try {
        await track.applyConstraints({
          advanced: [{ focusMode: "continuous" }],
        });
      } catch {
        // The camera keeps whatever focus mode it had.
      }
    };

    const poll = async () => {
      if (cancelled) return;

      const video = findVideo(containerRef.current);
      const track = video?.srcObject?.getVideoTracks?.()[0];

      if (track && track.readyState === "live") {
        await applyFocus(track);
        return;
      }

      attempts += 1;
      if (attempts < MAX_ATTEMPTS) {
        timeoutId = window.setTimeout(poll, POLL_MS);
      }
    };

    timeoutId = window.setTimeout(poll, POLL_MS);

    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [containerRef, enabled]);
};

export default useContinuousAutofocus;
